import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { submitInquiry } from "../lib/api";

const initial = {
  full_name: "",
  nationality: "",
  phone: "",
  email: "",
  property_location: "",
  message: "",
};

export default function InquiryForm({
  apartment = null,
  source = null,
  title = null,
  subtitle = null,
}) {
  const [values, setValues] = useState(initial);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.full_name || !values.nationality || !values.phone || !values.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({
        ...values,
        email: values.email || null,
        property_location: values.property_location || null,
        apartment_id: apartment?.id || null,
        apartment_name: apartment?.name || null,
        source: source || (apartment ? "case-study" : "general"),
      });
      toast.success("Thank you — our practice lead will be in touch within one business day.");
      setValues(initial);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="inquiry-form"
      onSubmit={handleSubmit}
      className="rounded-2xl border border-[#D9CFC2]/60 bg-white p-8 shadow-[0_8px_30px_rgba(26,29,26,0.04)] md:p-10"
    >
      <div className="mb-7">
        <span className="overline">Request Consultation</span>
        <h3 className="mt-2 font-[Cormorant_Garamond] text-3xl text-[#1A1D1A]">
          {title || (apartment ? "Discuss a similar mandate" : "Speak with the practice")}
        </h3>
        <p className="mt-2 text-sm text-[#4A524C]">
          {subtitle ||
            "Share a few details about your property or portfolio and our practice lead will respond within one business day."}
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="full_name" className="label-luxe">
            Full Name *
          </label>
          <input
            id="full_name"
            name="full_name"
            data-testid="inquiry-input-full-name"
            value={values.full_name}
            onChange={handleChange}
            placeholder="Jane Whitfield"
            className="input-luxe"
            required
          />
        </div>
        <div>
          <label htmlFor="nationality" className="label-luxe">
            Nationality *
          </label>
          <input
            id="nationality"
            name="nationality"
            data-testid="inquiry-input-nationality"
            value={values.nationality}
            onChange={handleChange}
            placeholder="British"
            className="input-luxe"
            required
          />
        </div>
        <div>
          <label htmlFor="phone" className="label-luxe">
            Phone Number *
          </label>
          <input
            id="phone"
            name="phone"
            data-testid="inquiry-input-phone"
            value={values.phone}
            onChange={handleChange}
            placeholder="+44 7700 900123"
            className="input-luxe"
            required
          />
        </div>
        <div>
          <label htmlFor="email" className="label-luxe">
            Email <span className="lowercase text-[#7A8C7B]">(optional)</span>
          </label>
          <input
            id="email"
            name="email"
            data-testid="inquiry-input-email"
            type="email"
            value={values.email}
            onChange={handleChange}
            placeholder="you@example.com"
            className="input-luxe"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="property_location" className="label-luxe">
          Property Location <span className="lowercase text-[#7A8C7B]">(optional)</span>
        </label>
        <input
          id="property_location"
          name="property_location"
          data-testid="inquiry-input-property-location"
          value={values.property_location}
          onChange={handleChange}
          placeholder="e.g. Kensington, London · 2-bedroom apartment"
          className="input-luxe"
        />
      </div>

      <div className="mt-5">
        <label htmlFor="message" className="label-luxe">
          How can we help? *
        </label>
        <textarea
          id="message"
          name="message"
          data-testid="inquiry-input-message"
          value={values.message}
          onChange={handleChange}
          placeholder="Tell us about your property or portfolio, current status, and what you'd like to achieve."
          rows={5}
          className="input-luxe resize-none"
          required
        />
      </div>

      <button
        type="submit"
        data-testid="inquiry-submit-button"
        disabled={loading}
        className="btn-primary mt-8 w-full text-base"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send size={16} />
            Request Consultation
          </>
        )}
      </button>

      <p className="mt-4 text-center text-xs text-[#4A524C]">
        Discovery calls are confidential and free of charge · replies to{" "}
        <a
          href="mailto:info@sagita-llc.com"
          className="text-[#967A45] underline-offset-4 hover:underline"
        >
          info@sagita-llc.com
        </a>
      </p>
    </form>
  );
}
